import { useState, useEffect, useRef } from "react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Save, Plus, Trash2, Upload, Image as ImageIcon } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

function Field({ label, value, onChange, multiline, small }) {
  return (
    <div className={small ? "" : "mb-4"}>
      <Label className="text-xs font-sans font-medium text-gray-500 uppercase tracking-wider mb-1 block">{label}</Label>
      {multiline ? (
        <Textarea value={value || ""} onChange={(e) => onChange(e.target.value)} className="border-gray-200 rounded-sm font-sans text-sm min-h-[80px]" />
      ) : (
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} className="border-gray-200 rounded-sm h-9 font-sans text-sm" />
      )}
    </div>
  );
}

function ImageUpload({ label, currentUrl, onUploaded }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/api/upload/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(`${API}/api/files/${data.path}`);
    } catch (err) {
      alert("Upload failed: " + (err.response?.data?.detail || err.message));
    }
    setUploading(false);
  };

  return (
    <div className="mb-4">
      <Label className="text-xs font-sans font-medium text-gray-500 uppercase tracking-wider mb-1 block">{label}</Label>
      <div className="flex items-center gap-3">
        {currentUrl ? (
          <img src={currentUrl} alt="" className="w-16 h-16 object-cover border border-gray-200 rounded-sm" />
        ) : (
          <div className="w-16 h-16 bg-gray-100 border border-gray-200 rounded-sm flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-gray-300" />
          </div>
        )}
        <div>
          <button onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-1 text-xs font-sans text-navy-900 hover:text-gold-500 transition-colors disabled:opacity-50">
            <Upload className="w-3 h-3" /> {uploading ? "Uploading..." : "Upload Image"}
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
          {currentUrl && (
            <button onClick={() => onUploaded("")} className="text-xs font-sans text-red-400 hover:text-red-600 mt-1">Remove</button>
          )}
        </div>
      </div>
    </div>
  );
}

function ArrayEditor({ label, items, onChange, fields }) {
  const addItem = () => {
    const empty = {};
    fields.forEach(f => { empty[f.key] = ""; });
    onChange([...items, empty]);
  };
  const updateItem = (i, key, val) => {
    const copy = [...items];
    copy[i] = { ...copy[i], [key]: val };
    onChange(copy);
  };
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs font-sans font-medium text-gray-500 uppercase tracking-wider">{label}</Label>
        <button onClick={addItem} className="flex items-center gap-1 text-xs text-navy-900 hover:text-gold-500 font-sans"><Plus className="w-3 h-3" /> Add</button>
      </div>
      <div className="space-y-3">
        {(items || []).map((item, i) => (
          <div key={i} className="border border-gray-200 p-3 relative">
            <button onClick={() => removeItem(i)} className="absolute top-2 right-2 text-gray-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pr-6">
              {fields.map(f => (
                <div key={f.key}>
                  <Label className="text-[10px] font-sans text-gray-400 mb-0.5 block">{f.label}</Label>
                  {f.multiline ? (
                    <Textarea value={item[f.key] || ""} onChange={(e) => updateItem(i, f.key, e.target.value)} className="border-gray-200 rounded-sm font-sans text-xs min-h-[50px]" />
                  ) : (
                    <Input value={item[f.key] || ""} onChange={(e) => updateItem(i, f.key, e.target.value)} className="border-gray-200 rounded-sm h-8 font-sans text-xs" />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StringArrayEditor({ label, items, onChange }) {
  const addItem = () => onChange([...(items || []), ""]);
  const updateItem = (i, val) => { const c = [...items]; c[i] = val; onChange(c); };
  const removeItem = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-xs font-sans font-medium text-gray-500 uppercase tracking-wider">{label}</Label>
        <button onClick={addItem} className="flex items-center gap-1 text-xs text-navy-900 hover:text-gold-500 font-sans"><Plus className="w-3 h-3" /> Add</button>
      </div>
      {(items || []).map((item, i) => (
        <div key={i} className="flex items-center gap-2 mb-1">
          <Input value={item} onChange={(e) => updateItem(i, e.target.value)} className="border-gray-200 rounded-sm h-8 font-sans text-xs flex-1" />
          <button onClick={() => removeItem(i)} className="text-gray-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      ))}
    </div>
  );
}

function PageSection({ title, children }) {
  return (
    <div className="mb-8 pb-8 border-b border-gray-100 last:border-0">
      <h3 className="font-serif text-lg text-navy-900 mb-4">{title}</h3>
      {children}
    </div>
  );
}

export default function AdminContent() {
  const [allContent, setAllContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState("");

  useEffect(() => {
    axios.get(`${API}/api/content`)
      .then(r => setAllContent(r.data))
      .finally(() => setLoading(false));
  }, []);

  const update = (pageKey, field, value) => {
    setAllContent(prev => ({
      ...prev,
      [pageKey]: { ...prev[pageKey], [field]: value }
    }));
  };

  const updateNested = (pageKey, path, value) => {
    setAllContent(prev => {
      const page = { ...prev[pageKey] };
      const keys = path.split(".");
      let obj = page;
      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return { ...prev, [pageKey]: page };
    });
  };

  const savePage = async (pageKey) => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/content/${pageKey}`, { data: allContent[pageKey] });
      setSaved(pageKey);
      setTimeout(() => setSaved(""), 2000);
    } catch (err) {
      alert("Save failed: " + (err.response?.data?.detail || err.message));
    }
    setSaving(false);
  };

  if (loading) return <p className="text-gray-400 font-sans text-sm">Loading content...</p>;

  const b = allContent.branding || {};
  const h = allContent.homepage || {};
  const a = allContent.about || {};
  const s = allContent.services || {};
  const sec = allContent.sectors || {};
  const c = allContent.contact || {};
  const f = allContent.footer || {};

  const SaveBtn = ({ pageKey }) => (
    <button onClick={() => savePage(pageKey)} disabled={saving} data-testid={`save-${pageKey}`} className="flex items-center gap-2 bg-navy-900 text-white px-5 py-2.5 rounded-sm text-sm font-sans font-medium hover:bg-navy-800 transition-colors disabled:opacity-50 mb-6">
      <Save className="w-4 h-4" /> {saved === pageKey ? "Saved!" : saving ? "Saving..." : "Save Changes"}
    </button>
  );

  return (
    <div data-testid="admin-content-page">
      <h1 className="font-serif text-2xl text-navy-900 mb-6">Site Content Editor</h1>
      <Tabs defaultValue="branding">
        <TabsList className="flex flex-wrap gap-1 mb-6 bg-gray-100 p-1 rounded-sm">
          {["branding", "homepage", "about", "services", "sectors", "contact", "footer"].map(t => (
            <TabsTrigger key={t} value={t} className="px-4 py-2 text-xs font-sans capitalize rounded-sm data-[state=active]:bg-white data-[state=active]:text-navy-900 data-[state=active]:shadow-sm">{t}</TabsTrigger>
          ))}
        </TabsList>

        {/* ── BRANDING ── */}
        <TabsContent value="branding">
          <SaveBtn pageKey="branding" />
          <PageSection title="Company Identity">
            <Field label="Company Name (Short)" value={b.company_name} onChange={v => update("branding", "company_name", v)} />
            <Field label="Company Full Name" value={b.company_full_name} onChange={v => update("branding", "company_full_name", v)} />
            <Field label="Text next to Company Name in header (leave empty to hide)" value={b.tagline} onChange={v => update("branding", "tagline", v)} />
            <ImageUpload label="Company Logo" currentUrl={b.logo_url} onUploaded={v => update("branding", "logo_url", v)} />
            <div className="mb-4">
              <Label className="text-xs font-sans font-medium text-gray-500 uppercase tracking-wider mb-2 block">Logo Size</Label>
              <div className="flex gap-2">
                {[{value: "small", label: "S"}, {value: "medium", label: "M"}, {value: "large", label: "L"}, {value: "xlarge", label: "XL"}, {value: "xxlarge", label: "2XL"}, {value: "xxxlarge", label: "3XL"}].map(opt => (
                  <button key={opt.value} onClick={() => update("branding", "logo_size", opt.value)} className={`px-4 py-2 text-xs font-sans rounded-sm border transition-colors ${(b.logo_size || "medium") === opt.value ? 'bg-navy-900 text-white border-navy-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>{opt.label}</button>
                ))}
              </div>
              <p className="text-xs text-gray-400 font-sans mt-1">Controls the logo height in the header and footer</p>
            </div>
          </PageSection>
          <PageSection title="Contact Details">
            <Field label="Phone Number" value={b.phone} onChange={v => update("branding", "phone", v)} />
            <Field label="Email" value={b.email} onChange={v => update("branding", "email", v)} />
            <Field label="WhatsApp Number (no +, no spaces)" value={b.whatsapp_number} onChange={v => update("branding", "whatsapp_number", v)} />
            <Field label="WhatsApp Default Message" value={b.whatsapp_message} onChange={v => update("branding", "whatsapp_message", v)} />
          </PageSection>
        </TabsContent>

        {/* ── HOMEPAGE ── */}
        <TabsContent value="homepage">
          <SaveBtn pageKey="homepage" />
          <PageSection title="Hero Section">
            <Field label="Top Label" value={h.hero_label} onChange={v => update("homepage", "hero_label", v)} />
            <Field label="Headline (use \\n for line break)" value={h.hero_headline} onChange={v => update("homepage", "hero_headline", v)} multiline />
            <Field label="Subtext" value={h.hero_subtext} onChange={v => update("homepage", "hero_subtext", v)} multiline />
            <Field label="CTA Button Text" value={h.hero_cta_text} onChange={v => update("homepage", "hero_cta_text", v)} />
            <Field label="Secondary CTA Text" value={h.hero_cta_secondary} onChange={v => update("homepage", "hero_cta_secondary", v)} />
          </PageSection>
          <PageSection title="Impact Numbers">
            <ArrayEditor label="Stats" items={h.impact_numbers || []} onChange={v => update("homepage", "impact_numbers", v)} fields={[{ key: "value", label: "Value" }, { key: "label", label: "Label" }]} />
          </PageSection>
          <PageSection title="About Snapshot">
            <Field label="Section Label" value={h.about_label} onChange={v => update("homepage", "about_label", v)} />
            <Field label="Headline" value={h.about_headline} onChange={v => update("homepage", "about_headline", v)} />
            <Field label="Main Text" value={h.about_text} onChange={v => update("homepage", "about_text", v)} multiline />
            <Field label="Secondary Text" value={h.about_subtext} onChange={v => update("homepage", "about_subtext", v)} multiline />
            <Field label="Stat Value (e.g. 15+ Years)" value={h.about_stat_value} onChange={v => update("homepage", "about_stat_value", v)} />
            <Field label="Stat Description" value={h.about_stat_label} onChange={v => update("homepage", "about_stat_label", v)} />
          </PageSection>
          <PageSection title="Services Section">
            <Field label="Section Label" value={h.services_label} onChange={v => update("homepage", "services_label", v)} />
            <Field label="Headline" value={h.services_headline} onChange={v => update("homepage", "services_headline", v)} />
            <ArrayEditor label="Service Cards" items={h.services || []} onChange={v => update("homepage", "services", v)} fields={[{ key: "title", label: "Title" }, { key: "desc", label: "Description", multiline: true }, { key: "path", label: "Link Path" }]} />
          </PageSection>
          <PageSection title="Sectors Section">
            <Field label="Section Label" value={h.sectors_label} onChange={v => update("homepage", "sectors_label", v)} />
            <Field label="Headline" value={h.sectors_headline} onChange={v => update("homepage", "sectors_headline", v)} />
          </PageSection>
          <PageSection title="Why Capstone / Differentiators">
            <Field label="Section Label" value={h.differentiators_label} onChange={v => update("homepage", "differentiators_label", v)} />
            <Field label="Headline" value={h.differentiators_headline} onChange={v => update("homepage", "differentiators_headline", v)} />
            <ArrayEditor label="Differentiators" items={h.differentiators || []} onChange={v => update("homepage", "differentiators", v)} fields={[{ key: "number", label: "Number" }, { key: "title", label: "Title" }, { key: "desc", label: "Description", multiline: true }]} />
          </PageSection>
          <PageSection title="Leadership Credibility">
            <Field label="Label" value={h.credibility_label} onChange={v => update("homepage", "credibility_label", v)} />
            <StringArrayEditor label="Company Names" items={h.credibility_logos || []} onChange={v => update("homepage", "credibility_logos", v)} />
          </PageSection>
          <PageSection title="Global Presence">
            <Field label="Section Label" value={h.global_label} onChange={v => update("homepage", "global_label", v)} />
            <Field label="Headline" value={h.global_headline} onChange={v => update("homepage", "global_headline", v)} multiline />
            <Field label="Description" value={h.global_text} onChange={v => update("homepage", "global_text", v)} multiline />
            <StringArrayEditor label="Location List" items={h.global_locations || []} onChange={v => update("homepage", "global_locations", v)} />
            <ArrayEditor label="Global Stats" items={h.global_stats || []} onChange={v => update("homepage", "global_stats", v)} fields={[{ key: "value", label: "Value" }, { key: "label", label: "Label" }]} />
          </PageSection>
        </TabsContent>

        {/* ── ABOUT ── */}
        <TabsContent value="about">
          <SaveBtn pageKey="about" />
          <PageSection title="Hero">
            <Field label="Label" value={a.hero_label} onChange={v => update("about", "hero_label", v)} />
            <Field label="Headline" value={a.hero_headline} onChange={v => update("about", "hero_headline", v)} />
            <Field label="Subtext" value={a.hero_subtext} onChange={v => update("about", "hero_subtext", v)} multiline />
          </PageSection>
          <PageSection title="Company Story">
            <Field label="Section Label" value={a.story_label} onChange={v => update("about", "story_label", v)} />
            <Field label="Headline" value={a.story_headline} onChange={v => update("about", "story_headline", v)} />
            <StringArrayEditor label="Story Paragraphs" items={a.story_paragraphs || []} onChange={v => update("about", "story_paragraphs", v)} />
          </PageSection>
          <PageSection title="Vision & Mission">
            <Field label="Vision Title" value={a.vision_title} onChange={v => update("about", "vision_title", v)} />
            <Field label="Vision Text" value={a.vision_text} onChange={v => update("about", "vision_text", v)} multiline />
            <Field label="Mission Title" value={a.mission_title} onChange={v => update("about", "mission_title", v)} />
            <Field label="Mission Text" value={a.mission_text} onChange={v => update("about", "mission_text", v)} multiline />
          </PageSection>
          <PageSection title="Core Values">
            <Field label="Section Label" value={a.values_label} onChange={v => update("about", "values_label", v)} />
            <Field label="Headline" value={a.values_headline} onChange={v => update("about", "values_headline", v)} />
            <ArrayEditor label="Values" items={a.values || []} onChange={v => update("about", "values", v)} fields={[{ key: "title", label: "Title" }, { key: "desc", label: "Description", multiline: true }]} />
          </PageSection>
          <PageSection title="Global Section">
            <Field label="Headline" value={a.global_headline} onChange={v => update("about", "global_headline", v)} />
            <Field label="Text" value={a.global_text} onChange={v => update("about", "global_text", v)} multiline />
          </PageSection>
        </TabsContent>

        {/* ── SERVICES ── */}
        <TabsContent value="services">
          <SaveBtn pageKey="services" />
          <PageSection title="Services Overview">
            <Field label="Hero Label" value={s.hero_label} onChange={v => update("services", "hero_label", v)} />
            <Field label="Hero Headline" value={s.hero_headline} onChange={v => update("services", "hero_headline", v)} />
          </PageSection>
          {["capital_raising", "asset_management", "strategic_advisory"].map(sKey => {
            const svc = s[sKey] || {};
            const upd = (field, val) => updateNested("services", `${sKey}.${field}`, val);
            return (
              <PageSection key={sKey} title={svc.title || sKey.replace("_", " ")}>
                <Field label="Title" value={svc.title} onChange={v => upd("title", v)} />
                <Field label="Subtitle" value={svc.subtitle} onChange={v => upd("subtitle", v)} />
                <Field label="Description" value={svc.description} onChange={v => upd("description", v)} multiline />
                <ArrayEditor label="Offerings" items={svc.offerings || []} onChange={v => upd("offerings", v)} fields={[{ key: "title", label: "Title" }, { key: "desc", label: "Description", multiline: true }]} />
                <ArrayEditor label="Process Steps" items={svc.process || []} onChange={v => upd("process", v)} fields={[{ key: "step", label: "Step #" }, { key: "title", label: "Title" }, { key: "desc", label: "Description", multiline: true }]} />
                <StringArrayEditor label="Use Cases" items={svc.use_cases || []} onChange={v => upd("use_cases", v)} />
              </PageSection>
            );
          })}
        </TabsContent>

        {/* ── SECTORS ── */}
        <TabsContent value="sectors">
          <SaveBtn pageKey="sectors" />
          <PageSection title="Sectors Page Hero">
            <Field label="Label" value={sec.hero_label} onChange={v => update("sectors", "hero_label", v)} />
            <Field label="Headline" value={sec.hero_headline} onChange={v => update("sectors", "hero_headline", v)} />
            <Field label="Subtext" value={sec.hero_subtext} onChange={v => update("sectors", "hero_subtext", v)} multiline />
          </PageSection>
          {(sec.items || []).map((item, i) => (
            <PageSection key={i} title={`Sector: ${item.label || `#${i + 1}`}`}>
              <Field label="Label" value={item.label} onChange={v => {
                const copy = [...(sec.items || [])];
                copy[i] = { ...copy[i], label: v };
                update("sectors", "items", copy);
              }} />
              <StringArrayEditor label="Challenges" items={item.challenges || []} onChange={v => {
                const copy = [...(sec.items || [])];
                copy[i] = { ...copy[i], challenges: v };
                update("sectors", "items", copy);
              }} />
              <StringArrayEditor label="Solutions" items={item.solutions || []} onChange={v => {
                const copy = [...(sec.items || [])];
                copy[i] = { ...copy[i], solutions: v };
                update("sectors", "items", copy);
              }} />
              <Field label="Example Quote" value={item.example} onChange={v => {
                const copy = [...(sec.items || [])];
                copy[i] = { ...copy[i], example: v };
                update("sectors", "items", copy);
              }} multiline />
            </PageSection>
          ))}
        </TabsContent>

        {/* ── CONTACT ── */}
        <TabsContent value="contact">
          <SaveBtn pageKey="contact" />
          <PageSection title="Contact Page Hero">
            <Field label="Label" value={c.hero_label} onChange={v => update("contact", "hero_label", v)} />
            <Field label="Headline" value={c.hero_headline} onChange={v => update("contact", "hero_headline", v)} />
            <Field label="Subtext" value={c.hero_subtext} onChange={v => update("contact", "hero_subtext", v)} multiline />
          </PageSection>
          <PageSection title="Form Settings">
            <Field label="Form Headline" value={c.form_headline} onChange={v => update("contact", "form_headline", v)} />
            <Field label="Form Subtext" value={c.form_subtext} onChange={v => update("contact", "form_subtext", v)} />
          </PageSection>
          <PageSection title="Office Locations">
            <ArrayEditor label="Offices" items={c.offices || []} onChange={v => update("contact", "offices", v)} fields={[
              { key: "city", label: "City" }, { key: "tag", label: "Tag (e.g. Headquarters)" },
              { key: "address", label: "Full Address" }, { key: "phone", label: "Phone" },
              { key: "email", label: "Email" }, { key: "hours", label: "Business Hours" },
            ]} />
          </PageSection>
        </TabsContent>

        {/* ── FOOTER ── */}
        <TabsContent value="footer">
          <SaveBtn pageKey="footer" />
          <PageSection title="CTA Banner">
            <Field label="Label" value={f.cta_label} onChange={v => update("footer", "cta_label", v)} />
            <Field label="Headline" value={f.cta_headline} onChange={v => update("footer", "cta_headline", v)} />
            <Field label="Text" value={f.cta_text} onChange={v => update("footer", "cta_text", v)} multiline />
            <Field label="Button Text" value={f.cta_button} onChange={v => update("footer", "cta_button", v)} />
          </PageSection>
          <PageSection title="Footer Info">
            <Field label="Description" value={f.description} onChange={v => update("footer", "description", v)} multiline />
            <Field label="Copyright Text" value={f.copyright} onChange={v => update("footer", "copyright", v)} />
          </PageSection>
        </TabsContent>
      </Tabs>
    </div>
  );
}
